/**
 * The filter box and the filter buttons, kept in sync both ways. Buttons apply
 * at once; the box applies once typing pauses (or on Enter, or on leaving it).
 * Out of focus, the box shows each applied filter as its own block (× drops
 * it); clicking it edits the text, still drawn as blocks while typed. A box that cannot be read shows why
 * under it, and the last good filters stay.
 */

import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { errorText } from "@/components/ui/states";
import { useDebounced } from "./use-debounced";
import { FilterButton, type FilterChoice } from "./filter-button";
import { FilterChips, QueryHighlight } from "./filter-chips";
import { useTraceFilters } from "./hooks";
import { parse, toText, type ObservationFilters, type TraceQuery } from "./trace-query";

type Counted = Array<{ value: string; count: number }> | undefined;
/** Langfuse's observation types, listed even when none are in the period. */
const TYPES = [
  "AGENT",
  "SPAN",
  "GENERATION",
  "TOOL",
  "EVENT",
  "CHAIN",
  "RETRIEVER",
  "EVALUATOR",
  "EMBEDDING",
  "GUARDRAIL",
];
const EXAMPLE = "type:GENERATION,TOOL name:extract-fields level:ERROR";

/** Each value seen, with its count, plus any value picked that the period lacks. */
function choices(counted: Counted, chosen: string[], always: string[] = []): FilterChoice[] {
  const found = new Map((counted ?? []).map((c) => [c.value, c.count]));
  const values = [...found.keys(), ...[...always, ...chosen].filter((v) => !found.has(v))];
  return [...new Set(values)].map((v) => ({ value: v, label: v, count: found.get(v) ?? 0 }));
}

const same = (a: ObservationFilters, b: ObservationFilters) => toText(a) === toText(b);

/** Yes or No, each with its count. Unset (Clear) is no root filter, like every other button. */
function rootChoices(counted: Counted): FilterChoice[] {
  const count = (v: string) => counted?.find((c) => c.value === v)?.count ?? 0;
  return [
    { value: "true", label: "Yes", count: count("true") },
    { value: "false", label: "No", count: count("false") },
  ];
}

export function ObservationFilters({
  sourceId,
  value,
  onChange,
  nameButton = true,
}: {
  sourceId: string;
  value: TraceQuery;
  onChange: (next: TraceQuery) => void;
  /** The step-name button: off where a whole agent's runs are picked (a live
   * dataset), on where one step is (an evaluator's sample). `name:` still types. */
  nameButton?: boolean;
}) {
  const id = useId();
  const facets = useTraceFilters(sourceId, value);
  const options = facets.data;
  // Langfuse judges the columns: one it does not have comes back as an error.
  const refused = facets.isError
    ? errorText(facets.error, "The trace source could not use this filter.")
    : null;
  /** What is typed in the box; `null` while it shows the applied filters. */
  const [text, setText] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);
  const box = useRef<HTMLTextAreaElement>(null);
  const read = text === null ? null : parse(text);
  const typed = read && "filters" in read ? read.filters : null;
  const settled = useDebounced(typed);
  const apply = (f: ObservationFilters) => !same(f, value) && onChange({ ...value, ...f });

  // The box is an input the user types into: its settled text drives the filters.
  useEffect(() => {
    if (settled) apply(settled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled]);

  const set = (patch: Partial<ObservationFilters>) => (setText(null), onChange({ ...value, ...patch }));
  const leave = () => typed && (apply(typed), setText(null));
  const on = toText(value) !== "";
  const showBlocks = on && !focused && text === null;
  const shown = text ?? toText(value);

  // While edited, the box grows with its text instead of scrolling sideways.
  useLayoutEffect(() => {
    const el = box.current;
    if (!el || showBlocks) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [shown, showBlocks]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <label htmlFor={id} className="text-xs font-semibold">
          Filter
        </label>
        <div className="relative">
          {!showBlocks && (
            <div
              aria-hidden
              className="inp pointer-events-none absolute inset-0 !h-auto overflow-hidden border-transparent !py-2 font-mono
                         text-xs leading-5 whitespace-pre-wrap [overflow-wrap:break-word]"
            >
              <QueryHighlight text={shown} />
            </div>
          )}
          <textarea
            ref={box}
            id={id}
            rows={1}
            spellCheck={false}
            autoComplete="off"
            aria-invalid={read !== null && "error" in read}
            aria-describedby={`${id}-help`}
            className={`inp block w-full resize-none font-mono text-xs leading-5 ${
              showBlocks
                ? "absolute inset-0 !h-full !min-h-0 overflow-hidden text-transparent"
                : "relative !min-h-9 !bg-transparent !py-2 text-transparent caret-ink [overflow-wrap:break-word]"
            }`}
            placeholder="traceName:my-agent type:GENERATION"
            value={shown}
            onChange={(e) => setText(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => (setFocused(false), leave())}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), leave())}
          />
          {/* Above the input, letting clicks through to it; only each × takes its own. */}
          {showBlocks && (
            <div className="pointer-events-none relative flex min-h-9 items-center px-2 py-1.5">
              <FilterChips value={value} onChange={set} />
            </div>
          )}
        </div>
        {read && "error" in read ? (
          <span id={`${id}-help`} role="alert" className="text-xs text-err">
            {read.error}
          </span>
        ) : refused ? (
          <span id={`${id}-help`} role="alert" className="text-xs text-err">
            {refused}
          </span>
        ) : (
          <span id={`${id}-help`} className="text-xs text-muted">
            Type filters, or use the buttons below; any Langfuse column works. Example:{" "}
            <code className="font-mono">{EXAMPLE}</code>
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filters">
        <FilterButton
          label="Trace name"
          search
          choices={choices(options?.trace_names, value.traceNames)}
          chosen={value.traceNames}
          onChange={(traceNames) => set({ traceNames })}
        />
        <FilterButton
          label="Type"
          choices={choices(options?.types, value.types, TYPES)}
          chosen={value.types}
          onChange={(types) => set({ types })}
        />
        {nameButton && (
          <FilterButton
            label="Name"
            search
            choices={choices(options?.names, value.names)}
            chosen={value.names}
            onChange={(names) => set({ names })}
          />
        )}
        <FilterButton
          label="Root observation"
          single
          choices={rootChoices(options?.roots)}
          chosen={value.root === null ? [] : [String(value.root)]}
          onChange={([root]) => set({ root: root === "true" ? true : root === "false" ? false : null })}
        />
        <FilterButton
          label="Environment"
          choices={choices(options?.environments, value.environments)}
          chosen={value.environments}
          onChange={(environments) => set({ environments })}
        />
        {on && (
          <button
            type="button"
            className="text-xs text-muted underline hover:text-ink"
            onClick={() =>
              set({
                traceNames: [],
                types: [],
                names: [],
                root: null,
                environments: [],
                version: "",
                columns: {},
              })
            }
          >
            Clear all
          </button>
        )}
      </div>
    </div>
  );
}
